import React, { useEffect, useState } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import type { DragEndEvent } from '@dnd-kit/core';
import SubjectService from '../../services/SubjectService';
import { Button, Popconfirm, Switch, type TableColumnsType, message } from 'antd';
import SortableTable, { DragHandle } from '../../components/Common/SortableTable';
import DocumentService from '../../services/DocumentService';
import VideoService from '../../services/VideoService';
import { QuestionCircleOutlined } from '@ant-design/icons'
import { removeItem } from '../../services/RemoveService';
import { formatDate, secondToMinutes } from '../../utils';
import VimeoPlayer from '../../components/Subjects/Components/VimeoPlayer';
import VideoUpdate from './Update/VideoUpdate';
import DocumentCreate from './Create/DocumentCreate';
import DocumentUpdate from './Update/DocumentUpdate';
import ShareDocumentModal from './Share/ShareDocumentModal';

interface DataType {
  id: number;
  title: string;
  sortOrder: number;
  vimeoId?: string;
  documents?: any[];
  duration?: string;
  createdAt?: string;
  free?: boolean;
}

interface VideoListProps {
  subjectId: number;
  refresh: number;
}

const VideoList: React.FC<VideoListProps> = ({ subjectId, refresh }) => {
  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [loading, setLoading] = useState(false);
  const [switchLoading, setSwitchLoading] = useState<{ [key: number]: boolean }>({});
  const [currentNode, setCurrentNode] = useState<any>({ id: null, data: null, action: null, type: null });

  // --- Switch güncelleme fonksiyonu ---
  const handleSwitchChange = (record: DataType, checked: boolean) => {
    setSwitchLoading(prev => ({ ...prev, [record.id]: true }));

    record.free = checked;

    VideoService.update(record.id, record)
      .then(() => {
        setDataSource(prev =>
          prev.map(item =>
            item.id === record.id ? { ...item, free: checked } : item
          )
        );
        message.success('Ücretsiz durumu güncellendi');
      })
      .catch(() => message.error('Güncellenemedi'))
      .finally(() => {
        setSwitchLoading(prev => ({ ...prev, [record.id]: false }));
      });
  };

  const columns: TableColumnsType<DataType> = [
    { key: 'sort', align: 'center', width: 80, render: () => <DragHandle /> },
    { title: '#', dataIndex: 'id', width: 40 },
    { title: 'Başlık', dataIndex: 'title', width: 200 },
    {
      title: 'Video', dataIndex: 'vimeoId', width: 250,
      render: (vimeoId) => (
        <VimeoPlayer videoId={vimeoId}></VimeoPlayer>
      ),
    },
    // --- EKLENDİ: Ücretsiz Sütunu ---
    {
      title: '',
      dataIndex: 'free',
      key: 'free',
      align: 'center',
      width: 110,
      render: (free, record) => (
        <Switch
          checked={!!free}
          loading={!!switchLoading[record.id]}
          checkedChildren="Ücretsiz"
          unCheckedChildren="Ücretli"
          onChange={checked => handleSwitchChange(record, checked)}
        />
      )
    },
    {
      title: 'Dokümanlar',
      dataIndex: 'title',
      key: 'title',
      width: 80,
      render: (_: any, record: any) => (
        <ul>
          {record.documents?.map((item: any, index: any) => (
            <li key={index}>
              <ShareDocumentModal document={item} />
              <DocumentUpdate
                getTrigger={getData}
                setCurrentDocumentId={() => { }}
                documentId={item.id}
              />
              <Popconfirm
                title="Doküman Sil"
                description="Doküman silmek istediğinize emin misiniz?"
                okText="Evet"
                cancelText="Hayır"
                icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                onConfirm={() => setCurrentNode({ id: item.id, data: null, action: 'delete', type: 'document' })}
              >
                <Button danger onClick={e => e.stopPropagation()}>Sil</Button>
              </Popconfirm>
            </li>
          ))}
        </ul>
      )
    },
    {
      title: 'Süresi',
      dataIndex: 'duration',
      key: 'duration',
      width: 80,
      render: (duration) => (
        <>
          {secondToMinutes(duration)}
        </>
      ),
    },
    {
      title: 'Kayıt Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 80,
      render: (createdAt) => (
        <>
          {formatDate(createdAt)}
        </>
      ),
    },
    {
      title: 'İşlem',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (_: any, record: any) => (
        <>
          <DocumentCreate videoId={record.id} getData={getData}></DocumentCreate>
          <VideoUpdate id={record.id} subjectId={subjectId} getData={getData}></VideoUpdate>
          <Popconfirm
            title="Video Sil"
            description="Video silmek istediğinize emin misiniz?"
            okText="Evet"
            cancelText="Hayır"
            icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
            onConfirm={() => setCurrentNode({ id: record.id, data: null, action: 'delete', type: 'video' })}
          >
            <Button danger onClick={e => e.stopPropagation()}>Sil</Button>
          </Popconfirm>
        </>
      )
    },
  ];

  useEffect(() => {
    if (currentNode?.action === 'delete') {
      removeItem(currentNode).then(() => {
        getData();
      })
        .catch((error) => {
          console.error('Silme işlemi başarısız:', error);
        });
    }
    // eslint-disable-next-line
  }, [currentNode]);

  useEffect(() => {
    getData();
    // eslint-disable-next-line
  }, [subjectId, refresh]);

  const getData = () => {
    setLoading(true);
    SubjectService.getVideosBySubjectId(subjectId)
      .then((response: any) => {
        setDataSource(response.data);
        setLoading(false);
      })
      .catch((e: Error) => {
        console.log(e);
        setDataSource([]);
        setLoading(false);
      });
  };

  const updateSortOrders = (data: any) => {
    setLoading(true);
    VideoService.updateSortOrders(data)
      .then(() => {
        getData();
      })
      .catch((e: Error) => {
        console.log(e);
        setLoading(false);
      });
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setDataSource((prevState) => {
        const activeIndex = prevState.findIndex((record) => record.id === active?.id);
        const overIndex = prevState.findIndex((record) => record.id === over?.id);
        const newState = arrayMove(prevState, activeIndex, overIndex);

        const updatedSortOrder = newState.map((item, index) => ({
          id: item.id,
          sortOrder: index + 1
        }));

        updateSortOrders(updatedSortOrder);

        return newState;
      });
    }
  };

  return (
    <SortableTable<DataType>
      loading={loading}
      columns={columns}
      dataSource={dataSource}
      onDragEnd={onDragEnd}
      defaultSortField="sortOrder"
    />
  );
};

export default VideoList;
